import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import { Calendar } from '@material-ui/pickers'
import { format as dateFormat, isSameDay, startOfDay } from 'date-fns'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { defaultDateFormat } from 'shared/constants/AppConst'

interface MultiDateCalendarProps {
    dates: Date[]
    onChange: (dates: Date[]) => void
    disabled?: boolean
}

const MultiDateCalendar: React.FC<MultiDateCalendarProps> = ({ dates, onChange, disabled = false }) => {
    const { messages } = useIntl()
    const [cursorDate, setCursorDate] = useState<Date>(dates[0] ?? new Date())
    const sortedDates = useMemo(() => [...dates].sort((a, b) => +a - +b), [dates])

    const toggleDate = (date: Date | null) => {
        if (!date) return
        const normalizedDate = startOfDay(date)
        const exists = dates.some(selectedDate => isSameDay(selectedDate, normalizedDate))
        const nextDates = exists
            ? dates.filter(selectedDate => !isSameDay(selectedDate, normalizedDate))
            : [...dates, normalizedDate]

        setCursorDate(normalizedDate)
        onChange(nextDates.sort((a, b) => +a - +b))
    }

    return (
        <Box>
            <Calendar
                date={cursorDate}
                onChange={toggleDate}
                shouldDisableDate={() => disabled}
                renderDay={(day, _selectedDate, _dayInCurrentMonth, dayComponent) =>
                    React.cloneElement(dayComponent, {
                        selected: Boolean(day && dates.some(selectedDate => isSameDay(selectedDate, day)))
                    })
                }
            />
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={1}>
                <Typography variant="body2" color="textSecondary">
                    {messages['offering.multiDate.selected']}: {dates.length}
                </Typography>
                <Button size="small" onClick={() => onChange([])} disabled={disabled || dates.length === 0}>
                    {messages['offering.multiDate.clearAll']}
                </Button>
            </Box>
            <Box display="flex" flexWrap="wrap">
                {sortedDates.map(date => (
                    <Chip
                        key={date.toISOString()}
                        label={dateFormat(date, defaultDateFormat)}
                        color="primary"
                        variant="outlined"
                        onDelete={disabled ? undefined : () => onChange(dates.filter(selectedDate => !isSameDay(selectedDate, date)))}
                        style={{ margin: 4 }}
                    />
                ))}
            </Box>
        </Box>
    )
}

export default MultiDateCalendar
